import datacatalog, {
    AssociationType,
    AssociationTypeIndex,
    DataCatalogVersions,
    FeatureTypeIndex,
    FkbFeatureType,
    FkbFeatureTypeIndex,
    NvdbFeatureType,
    ObjectListIndex,
    ObjectListItem
} from "./datacatalog-services";

export default async function init(datacatalog: datacatalog): Promise<datacatalog> {

    // Remove old data catalog in localStorage
    clearLegacyLocalStorage();

    const cache = await caches.open("datacatalog");

    // Get latest version information
    const versionRequest = new Request("/datacatalog/versions");
    const versionResponse = await fetch(versionRequest);
    if (!versionResponse.ok) {
        throw new Error("Failed to read Datakatalog version");
    }
    const readVersion: DataCatalogVersions = await versionResponse.clone().json();

    // Cache status
    let versionInCache = false;
    const cachedVersionResponse = await cache.match(versionRequest);
    let cachedVersion: DataCatalogVersions;
    if (cachedVersionResponse) {
        cachedVersion = await cachedVersionResponse.json();
    }

    if (cachedVersion && versionEquals(cachedVersion, readVersion)) {
        versionInCache = true;
        console.log("Using cache for data catalog");
    } else {
        // Clear cache
        let keys = await cache.keys();
        await Promise.all(keys.map(key => cache.delete(key)));
    }

    // Get resources in parallell
    const [nvdbTypes, fkbTypes, associations, fkbObjectListItems] = await Promise.all([
        resolveCacheableResource<NvdbFeatureType[]>(cache, "/datacatalog/featuretypes"),
        resolveCacheableResource<FkbFeatureType[]>(cache, "/datacatalog/fkb_featuretypes"),
        resolveCacheableResource<AssociationType[]>(cache, "/datacatalog/associationtype"),
        resolveCacheableResource<ObjectListItem[]>(cache, "/datacatalog/fkb_objectlist")
    ]);

    // Convert to indexed types
    const featureTypeIndex: FeatureTypeIndex = {};
    nvdbTypes.forEach(featureType => {
        featureTypeIndex[featureType.id] = featureType;
    });

    const fkbFeatureTypeIndex: FkbFeatureTypeIndex = {};
    fkbTypes.forEach(fkbType => {
        fkbFeatureTypeIndex[fkbType.id] = fkbType;
    });

    const associationTypeIndex: AssociationTypeIndex = {};
    associations.forEach(associationType => {
        associationTypeIndex[associationType.id] = associationType;
    });

    const fkbObjectList: ObjectListIndex = {};
    fkbObjectListItems.forEach(fkbObjectListItem => {
        fkbObjectList[fkbObjectListItem.id] = fkbObjectListItem;
    });

    // Update version in cache
    if (!versionInCache) {
        await cache.put(versionRequest, versionResponse);
    }

    datacatalog.init(readVersion, featureTypeIndex, fkbFeatureTypeIndex, associationTypeIndex, fkbObjectList);

    return datacatalog;
}
init.$inject = ["datacatalog"];

async function resolveCacheableResource<T>(cache: Cache, url: string): Promise<T> {
    const request = new Request(url);

    let response = await cache.match(request);

    if (!response) {
        response = await fetch(request);
        if (response.ok) {
            await cache.put(request, response.clone());
        } else {
            throw new Error("Data catalog resource '" + request.url + "' responded with status " + response.status);
        }
    }

    return response.json();
}

function versionEquals(a: DataCatalogVersions, b: DataCatalogVersions) {
    return a.NVDB === b.NVDB && a.NVDBID === b.NVDBID && a.FKB === b.FKB;
}

function clearLegacyLocalStorage() {
    let versionString = localStorage.getItem("ngStorage-datacatalogVersions");
    if (versionString) {
        let storedVersion: DataCatalogVersions = JSON.parse(versionString);
        localStorage.removeItem(`ngStorage-${storedVersion.NVDB}_featureTypes`);
        localStorage.removeItem(`ngStorage-${storedVersion.FKB}_fkb_featuretypes`);
        localStorage.removeItem(`ngStorage-${storedVersion.NVDB}_associationTypes`);
        localStorage.removeItem(`ngStorage-${storedVersion.FKB}_fkb_objectList`);
        localStorage.removeItem(`ngStorage-${storedVersion.FKB}_categories`);
        localStorage.removeItem("ngStorage-datacatalogVersions");
        console.log("Cleared legacy localStorage");
    }
}